import { clone, isEmpty, isNaN, isNumber, isObject, trim } from 'lodash';
import { convertFromUTCTo, durationBetween } from './DateTimeUtilities';
import { getSystemTime } from '../stores/SystemTimeStore';
import DATE_FORMATS from './DateTimeFormats';
import { getMeridiemTimeFormat, getTimeZone } from '../stores/SessionStore';

// This should be the only place in the UI that we see the string '--' defined. Everything else should reference this.
export const DEFAULT_INVALID_VALUE = '--';

// Define all formatter types here
export const FORMATTER_TYPES = {
    TEXT: 'text',
    INTEGER: 'integer',
    DECIMAL: 'decimal',
    PERCENT: 'percent',
    LOG: 'log',
    BITRATE: 'bitRate',
    MEGABITRATE: 'megaBitRate',
    BYTERATE: 'byteRate',
    BIT: 'bit',
    BYTE: 'byte',
    DATE: 'date',
    DATETIME: 'dateTime',
    TIMERANGE: 'timeRange',
    DURATION: 'duration',
    DURATIONSINCE: 'durationSince',
    DURATIONUNTIL: 'durationUntil',
};

// And to allow for generic formatting where you don't know the specific formatter ahead of time,
// this is the LUT for
const FORMATTER_TYPE_REGISTRY = {
    [FORMATTER_TYPES.TEXT]: textFormatter,
    [FORMATTER_TYPES.INTEGER]: integerFormatter,
    [FORMATTER_TYPES.DECIMAL]: decimalFormatter,
    [FORMATTER_TYPES.PERCENT]: percentFormatter,
    [FORMATTER_TYPES.LOG]: logFormatter,
    [FORMATTER_TYPES.BITRATE]: bitRateFormatter,
    [FORMATTER_TYPES.MEGABITRATE]: megaBitRateFormatter,
    [FORMATTER_TYPES.BYTERATE]: byteRateFormatter,
    [FORMATTER_TYPES.BIT]: bitFormatter,
    [FORMATTER_TYPES.BYTE]: byteFormatter,
    [FORMATTER_TYPES.DATE]: dateFormatter,
    [FORMATTER_TYPES.DATETIME]: dateTimeFormatter,
    [FORMATTER_TYPES.TIMERANGE]: timeRangeFormatter,
    [FORMATTER_TYPES.DURATION]: durationFormatter,
    [FORMATTER_TYPES.DURATIONSINCE]: durationSinceFormatter,
    [FORMATTER_TYPES.DURATIONUNTIL]: durationUntilFormatter,
};

const DEFAULT_BASE_FORMATTER_CONFIG = {
    nullValue: DEFAULT_INVALID_VALUE,
    undefinedValue: DEFAULT_INVALID_VALUE,
    nilValue: DEFAULT_INVALID_VALUE,
    nanValue: DEFAULT_INVALID_VALUE,
    emptyValue: DEFAULT_INVALID_VALUE,

    trimStrings: true,
    baseUnits: null,
    displayUnits: true,
    allowNegativeValues: true,
};

export const BASE_UNITS = {
    MILLISECONDS: 'ms',
    SECONDS: 's',
    MINUTES: 'm',
    HOURS: 'h',
    DAYS: 'd',
    BYTES: 'B',
    BITS: 'b',
    BYTES_PER_SEC: 'B/s',
    BITS_PER_SEC: 'b/s',
    MEGA_BITS_PER_SEC: 'Mbps',
    PERCENT: '%',
};

export const DURATION_UNITS = {
    ms: 1,
    s: 1000,
    m: 1000 * 60,
    h: 1000 * 60 * 60,
    d: 1000 * 60 * 60 * 24,
};

export default function format(value, config) {
    const { type } = config;

    if (!type || !FORMATTER_TYPE_REGISTRY[type]) {
        throw new Error(
            `FormattingUtilities.format(): Missing or invalid formatter type configuration!. Type: "${type}"`
        );
    }

    return FORMATTER_TYPE_REGISTRY[type](value, config);
}

export function getUnits(formatter) {
    return isObject(formatter)
        ? format(null, { ...formatter, returnUnitsOnly: true })
        : format(null, { type: formatter, returnUnitsOnly: true });
}

const TEXT_FORMATTER_DEFAULTS = { ...DEFAULT_BASE_FORMATTER_CONFIG };
export function textFormatter(value, config) {
    return doformat(value, config, TEXT_FORMATTER_DEFAULTS, (val) => String(val));
}

const INTEGER_FORMATTER_DEFAULTS = { ...DEFAULT_BASE_FORMATTER_CONFIG };
export function integerFormatter(value, config) {
    const intValue = isNumber(value) && !isNaN(value) ? parseInt(String(value), 10) : undefined;
    return doformat(intValue, config, INTEGER_FORMATTER_DEFAULTS, (val, cfg) => {
        const { ltSymbol, ltComparison, ltComparator } = cfg;

        // If we're less than the "less than" value, display it
        if (ltComparator && ltComparator(value, cfg)) {
            if (!ltSymbol && !ltComparison) {
                return val;
            }

            return `${ltSymbol}${ltComparison}`;
        }

        return val;
    });
}

const DECIMAL_FORMATTER_DEFAULTS = {
    forceDecimals: false,
    multiplier: 1,
    maxDecimals: 2,
    ltSymbol: '<',
    ltComparison: 0.01,
    ltComparator: (value, config) => value > 0 && value < config.ltComparison,

    ...DEFAULT_BASE_FORMATTER_CONFIG,
};

export function decimalFormatter(value, config) {
    return doformat(parseFloat(String(value)), config, DECIMAL_FORMATTER_DEFAULTS, $decimalFormatter);
}

const PERCENT_FORMATTER_DEFAULTS = {
    ...DECIMAL_FORMATTER_DEFAULTS,

    maxDecimals: 1,
    baseUnits: BASE_UNITS.PERCENT,
};

export function percentFormatter(value, config) {
    return doformat(value, config, PERCENT_FORMATTER_DEFAULTS, (v, c) => $decimalFormatter(v, c));
}

export const LOG_FORMATTER_DEFAULTS = {
    ...DECIMAL_FORMATTER_DEFAULTS,

    prefixBase: 1000,
    prefixes: ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'],
    maxDecimals: 1,
};

export function logFormatter(value, config) {
    return doformat(value, config, LOG_FORMATTER_DEFAULTS, $logFormatter);
}

const BIT_RATE_FORMATTER_DEFAULTS = {
    ...LOG_FORMATTER_DEFAULTS,
    baseUnits: BASE_UNITS.BITS_PER_SEC,
};

const MEGA_BIT_RATE_FORMATTER_DEFAULTS = {
    ...LOG_FORMATTER_DEFAULTS,
    baseUnits: BASE_UNITS.MEGA_BITS_PER_SEC,
};

export function bitRateFormatter(value, config) {
    return doformat(value, config, BIT_RATE_FORMATTER_DEFAULTS, $logFormatter);
}

export function megaBitRateFormatter(value, config) {
    return doformat(value, config, MEGA_BIT_RATE_FORMATTER_DEFAULTS, $logFormatter);
}

const BYTE_RATE_FORMATTER_DEFAULTS = {
    ...LOG_FORMATTER_DEFAULTS,
    baseUnits: BASE_UNITS.BYTES_PER_SEC,
};
export function byteRateFormatter(value, config) {
    return doformat(value, config, BYTE_RATE_FORMATTER_DEFAULTS, () => null);
}

const BIT_FORMATTER_DEFAULTS = {
    ...LOG_FORMATTER_DEFAULTS,
    baseUnits: BASE_UNITS.BITS,
};
export function bitFormatter(value, config) {
    return doformat(value, config, BIT_FORMATTER_DEFAULTS, () => null);
}

const BYTE_FORMATTER_DEFAULTS = {
    ...LOG_FORMATTER_DEFAULTS,
    baseUnits: BASE_UNITS.BYTES,
};
export function byteFormatter(value, config) {
    return doformat(value, config, BYTE_FORMATTER_DEFAULTS, $logFormatter);
}

const DATE_FORMATTER_DEFAULTS = {
    ...DEFAULT_BASE_FORMATTER_CONFIG,
    toFormat: DATE_FORMATS.DATE,
};
export function dateFormatter(value, config) {
    return doformat(value, config, DATE_FORMATTER_DEFAULTS, (val, cfg) => {
        const { timeZone = getTimeZone(), toFormat } = cfg;
        return convertFromUTCTo(val, timeZone).format(toFormat);
    });
}

export function dateTimeFormatter(value, config) {
    const DATETIME_FORMATTER_DEFAULTS = {
        ...DEFAULT_BASE_FORMATTER_CONFIG,
        toFormat: getMeridiemTimeFormat() ? DATE_FORMATS.DATETIME_SEC_12H_TZ : DATE_FORMATS.DATETIME_SEC_24H_TZ,
    };
    return doformat(value, config, DATETIME_FORMATTER_DEFAULTS, (val, cfg) => {
        const { timeZone = getTimeZone(), toFormat } = cfg;
        return convertFromUTCTo(val, timeZone).format(toFormat);
    });
}

const DURATION_SINCE_FORMATTER_DEFAULTS = {
    ...DEFAULT_BASE_FORMATTER_CONFIG,
    showSeconds: false,
    precision: 1,
};
export function durationSinceFormatter(value, config) {
    return doformat(value, config, DURATION_SINCE_FORMATTER_DEFAULTS, (val, cfg) => {
        const since = durationBetween(val, getSystemTime(), cfg.precision, cfg.showSeconds, DEFAULT_INVALID_VALUE);
        return `${since}${since !== DEFAULT_INVALID_VALUE ? ' ago' : ''}`;
    });
}

const DURATION_UNTIL_FORMATTER_DEFAULTS = {
    ...DEFAULT_BASE_FORMATTER_CONFIG,
    showSeconds: false,
    precision: 1,
};
export function durationUntilFormatter(value, config) {
    return doformat(value, config, DURATION_UNTIL_FORMATTER_DEFAULTS, (val, cfg) => {
        return `${durationBetween(getSystemTime(), val, cfg.precision, cfg.showSeconds, DEFAULT_INVALID_VALUE)}`;
    });
}

const TIMERANGE_FORMATTER_DEFAULTS = {
    ...DEFAULT_BASE_FORMATTER_CONFIG,
};
export function timeRangeFormatter(value, config) {
    const meridiemTimeFormat = getMeridiemTimeFormat();
    const DEFAULT = meridiemTimeFormat ? DATE_FORMATS.DATETIME_MIN_12H : DATE_FORMATS.DATETIME_MIN_24H;
    return doformat(value, config, TIMERANGE_FORMATTER_DEFAULTS, (val, cfg) => {
        const { timeZone = getTimeZone() } = cfg;
        const now = getSystemTime();
        const start = convertFromUTCTo(val.start, timeZone);
        const end = convertFromUTCTo(val.end, timeZone);
        let result = '';

        if (!start || !end) {
            return result;
        }

        // Check if the range spans a day boundary.
        if (start.year() === end.year() && start.dayOfYear() === end.dayOfYear()) {
            // The range does not span a day boundary, so only show the date of the start time, and only if the date is
            // not today.
            if (now.dayOfYear() !== start.dayOfYear()) {
                result = `${start.format(DEFAULT)} - ${end.format(
                    meridiemTimeFormat ? DATE_FORMATS.TIME_MIN_12H_TZ : DATE_FORMATS.TIME_MIN_24H_TZ
                )}`;
            } else {
                result = `${start.format(
                    meridiemTimeFormat ? DATE_FORMATS.TIME_MIN_12H : DATE_FORMATS.TIME_MIN_24H
                )} - ${end.format(meridiemTimeFormat ? DATE_FORMATS.TIME_MIN_12H_TZ : DATE_FORMATS.TIME_MIN_24H_TZ)}`;
            }
        } else {
            const startTz = start.format(DATE_FORMATS.TZ);
            const endTz = end.format(DATE_FORMATS.TZ);

            // The range spans a day boundary, so show the date for both the start and end.
            if (startTz !== endTz) {
                // The range spans a DST boundary, so show the time zone name for both start and end times.
                result = `${start.format(
                    meridiemTimeFormat ? DATE_FORMATS.DATETIME_MIN_12H_TZ : DATE_FORMATS.DATETIME_MIN_24H_TZ
                )} - ${end.format(
                    meridiemTimeFormat ? DATE_FORMATS.DATETIME_MIN_12H_TZ : DATE_FORMATS.DATETIME_MIN_24H_TZ
                )}`;
            } else {
                // The range does not span a DST boundary, so only show the time zone name on the end.
                result = `${start.format(DEFAULT)} - ${end.format(
                    meridiemTimeFormat ? DATE_FORMATS.DATETIME_MIN_12H_TZ : DATE_FORMATS.DATETIME_MIN_24H_TZ
                )}`;
            }
        }

        return result;
    });
}

const DURATION_FORMATTER_DEFAULTS = {
    ...DECIMAL_FORMATTER_DEFAULTS,
    baseUnits: BASE_UNITS.MILLISECONDS,
    fromUnits: BASE_UNITS.MILLISECONDS,
    allowNegativeValues: false,
};
export function durationFormatter(value, config) {
    return doformat(value, config, DURATION_FORMATTER_DEFAULTS, (val, cfg) => {
        let duration = parseFloat(value);

        const { fromUnits, baseUnits } = cfg;

        if (fromUnits !== baseUnits) {
            duration *= DURATION_UNITS[fromUnits]; // go to milliseconds
            duration /= DURATION_UNITS[baseUnits]; // and now go to whatever unit needed
        }

        return $decimalFormatter(duration, cfg);
    });
}

export function formatIfValid(inVal, config, formatterFn) {
    const { nullValue, undefinedValue, nilValue, nanValue, emptyValue, trimStrings, allowNegativeValues } =
        config || {};
    let value = inVal;

    if (typeof value === 'string' && trimStrings === true) value = trim(value);

    if (value === null && nullValue) return nullValue;

    if (value === undefined && undefinedValue) return undefinedValue;

    if ((value === null || value === undefined) && nilValue) return nilValue;

    if (isNumber(value) && isNaN(value) && nanValue) return nanValue;

    if (isNumber(value) && value < 0 && !allowNegativeValues && emptyValue) return emptyValue;

    if (value === '' && emptyValue) return emptyValue;

    return formatterFn(value, config);
}

function doformat(value, config, configDefaults, formatterFn) {
    const mergedConfig = { ...configDefaults, ...config };
    if (mergedConfig.returnUnitsOnly) {
        return mergedConfig.baseUnits;
    }

    return formatIfValid(value, mergedConfig, $unitWrapper);

    function $unitWrapper(val, cfg) {
        const wrapVal = formatterFn(val, cfg);

        if (cfg.displayUnits === true && cfg.baseUnits) {
            return `${wrapVal}${cfg.baseUnits}`;
        }

        return wrapVal;
    }
}

function $decimalFormatter(value, cfg) {
    const { maxDecimals, ltSymbol, ltComparison, ltComparator, multiplier, forceDecimals } = cfg;
    const decimalMultiplier = 10 ** maxDecimals;

    // If we're less than the "less than" value, display it
    if (ltComparator(value, cfg)) {
        return `${ltSymbol}${ltComparison}`;
    }

    // Convert to a value with decimals
    const wrapVal = Math.round(value * multiplier * decimalMultiplier) / decimalMultiplier;

    return forceDecimals === true ? wrapVal.toFixed(maxDecimals) : String(wrapVal);
}

function $logFormatter(value, cfg) {
    const { prefixes, prefixBase, multiplier = 1 } = cfg;
    let wrapVal = value * multiplier;
    let prefixIndex = 0;

    if (wrapVal !== 0) {
        prefixIndex = Math.floor(Math.log(wrapVal) / Math.log(prefixBase));
        prefixIndex = Math.max(0, Math.min(prefixIndex, prefixes.length - 1));
    }

    const prefix = prefixes[prefixIndex];
    wrapVal /= prefixBase ** prefixIndex;

    const normalizedCfg = clone(cfg);
    normalizedCfg.multiplier = 1;

    return $decimalFormatter(wrapVal, normalizedCfg) + prefix;
}

export function getDefaultDateFormat() {
    return getMeridiemTimeFormat() ? DATE_FORMATS.DATETIME_MIN_12H : DATE_FORMATS.DATETIME_MIN_24H;
}

export function getChartDateTimeLabelFormats() {
    return {
        minute: getMeridiemTimeFormat() ? DATE_FORMATS.CHART_HOUR12 : DATE_FORMATS.CHART_HOUR24,
        hour: getMeridiemTimeFormat() ? DATE_FORMATS.CHART_HOUR12 : DATE_FORMATS.CHART_HOUR24,
        day: DATE_FORMATS.CHART_DAY,
        week: DATE_FORMATS.CHART_DAY,
    };
}

export function standardizeTags(tags) {
    if (isEmpty(tags)) return DEFAULT_INVALID_VALUE;
    return tags.join(', ');
}

export function formatNumber(value, digits = 0) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}
